import Metadata from '@/models/Metadata';
import DownloadAdapter from '@/adapters/download';
import Base64Encoder from "@/util/base64encoder";
import FileAES from "@/util/fileAES";
import KeyUtility from "@/util/keyUtility";
import Chunking from "@/util/chunking";
import { saveAs } from 'file-saver';
import wait from "@/util/wait";
import Upload from "@/models/Upload";
import * as Sentry from "@sentry/browser";
import EncryptedFile from "@/models/EncryptedFile";
import TransferInteractionConfiguration from "@/models/TransferInteractionConfiguration";

export default class DownloadHandle {

    private id: string;
    private adapter: DownloadAdapter;
    private upload!: Upload;
    private meta!: Metadata;
    private static maxtriesForDownload: number = 3;

    constructor(uploadId: string) {
        this.adapter = new DownloadAdapter();
        this.id = uploadId;
    }

    static fromMetadata(uploadId: string, meta: Metadata) {
        const result: DownloadHandle = new DownloadHandle(uploadId);
        result.meta = meta;
        return result;
    }

    static fromUpload(upload: Upload) {
        const result: DownloadHandle = new DownloadHandle(upload.id);
        result.upload = upload;
        return result;
    }

    async downloadMetadata(): Promise<Upload | undefined> {
        try {
            const uploadFromBackend = await this.adapter.downloadMetadata(this.id);
            if (uploadFromBackend !== undefined) {
                this.upload = uploadFromBackend;
            }
            return uploadFromBackend;
        } catch (err) {
            Sentry.captureException(err);
            throw err;
        }
    }

    async decryptMetadataWithPasswordAndUrlSalt(password: string, urlSalt: string): Promise<boolean> {
        try {
            const saltb64: string = decodeURIComponent(urlSalt);
            const salt: Uint8Array = Base64Encoder.stringToBytes(saltb64);
            const passKey: CryptoKey = await KeyUtility.deriveKeyFromPassword(password, salt);
            return this.decryptMetadataWithCryptoKey(passKey);
        } catch (err) {
            Sentry.captureException(err);
            throw err;
        }
    }

    async decryptMetadataWithKeyFromUrl(urlKey: string): Promise<boolean> {
        try {
            const b64Key = decodeURIComponent(urlKey);
            return this.decryptMetadataWithBase64Key(b64Key);
        } catch (e) {
            Sentry.captureException(e);
            throw e;
        }
    }

    async decryptMetadataWithBase64Key(b64Key: string): Promise<boolean> {
        try {
            const metaKey: CryptoKey = await KeyUtility.loadKeyFromBase64(b64Key);
            return this.decryptMetadataWithCryptoKey(metaKey);
        } catch (e) {
            Sentry.captureException(e);
            throw e;
        }

    }

    async decryptMetadataWithCryptoKey(key: CryptoKey): Promise<boolean> {
        try {
            const ivBytes = Base64Encoder.stringToBytes(this.upload.iv);
            const decryptedMeta: Metadata | undefined =
                await Metadata.fromEncryptedBytesAsBase64(key, ivBytes, this.upload.encryptedMetadata);
            if (decryptedMeta) {
                this.meta = decryptedMeta;

                //Check if this is still the old format and contains only one File
                if (this.meta.filesize !== 0) {
                    //Transform legacy meta to new file list format:
                    const pseudoEncFile: EncryptedFile = new EncryptedFile();
                    pseudoEncFile.name = this.meta.filename;
                    pseudoEncFile.size = this.meta.filesize;
                    for (let i = 0; i < this.meta.chunks; i++) {
                        pseudoEncFile.chunks[i] = `${i}`;
                    }
                    pseudoEncFile.ivs = this.meta.b64IVs;

                    //Insert this as only file:
                    this.meta.files = [ pseudoEncFile ];
                }
                return true;
            } else {
                return false;
            }
        } catch (e) {
            console.error("Metadata processing failed", e.name);
            Sentry.captureException(e);
            return false;
        }
    }

    async downloadFile(fileIdx: number, progressFunction: (progress: number) => void): Promise<boolean> {
        try {
            const encFile: EncryptedFile = this.meta.files[fileIdx];
            const allocation = new Uint8Array(encFile.size);
            const chunking = new Chunking(encFile.size);

            const result = await this.downloadFileWithCallback(fileIdx, progressFunction, (data, index) => {
                allocation.set(data, chunking.getChunkBeginning(index));
            }, () => {});  // tslint:disable-line

            await wait(550); // Wait for the animation to complete before downloading the file!
            saveAs(new Blob([allocation]), encFile.name);
            return result;

        } catch (err) {
            Sentry.captureException(err);
            throw err;
        }
    }

    async downloadFileWithCallback(fileIdx: number, progressFunction: (progress: number) => void,
                                   handlerFunction: (data: Uint8Array, index: number) => void,
                                   doneFunction: () => void): Promise<boolean> {

        try {
            if (!this.meta) {
                return false;
            }

            const encFile: EncryptedFile = this.meta.files[fileIdx];

            const chunkCount: number = encFile.chunks.length;
            const chunkIds: string[] = encFile.chunks;
            const ivs: string[] = encFile.ivs;

            const fileKey = await KeyUtility.loadKeyFromBase64(this.meta.keyAsBase64);
            const aes = new FileAES(fileKey);

            const totalPercent = chunkCount * 100;
            for (let i = 0; i < chunkIds.length; i++) {
                const chunkId: string = chunkIds[i];

                let result: Uint8Array | null = null;

                try {
                    const encrypted = await this.adapter.downloadChunk(this.id, chunkId, ((progressEvent: {loaded: number, total: number}) => {
                        const percentOfThisChunk: number = (progressEvent.loaded / progressEvent.total) * 100;
                        const absolutePercentDone = i * 100 + percentOfThisChunk;
                        const percentDone = (absolutePercentDone / totalPercent) * 100;
                        progressFunction(Math.ceil(percentDone));
                    }));
                    const ivBytes = Base64Encoder.stringToBytes(ivs[i]);
                    const plaintext = await aes.decryptChunk(encrypted, ivBytes);
                    result = new Uint8Array(plaintext);
                } catch (e) {
                    result = null;
                    Sentry.captureException(e);
                }

                if (result === null) {
                    throw new Error(`Fehler beim Herunterladen der Datei: Teil ${i} ` +
                        `konnte nicht heruntergeladen werden.`);
                } else {
                    handlerFunction(result, i);
                }
            }
            doneFunction();
            return true;
        } catch (e) {
            Sentry.captureException(e);
            throw e;
        }
    }

    getMeta(): Metadata {
        return this.meta;
    }

    getInteractionConfig(): TransferInteractionConfiguration {
        return this.meta.interactionConfiguration || new TransferInteractionConfiguration(); //Default values if not submitted!
    }

    getId(): string {
        return this.id;
    }
}
